import type { ClauseType, LongSentenceReason } from '../lib/longSentence';
import type {
  AiProvider,
  CefrLevel,
  SrsRating,
  StudyMode,
} from './index';
import type {
  ReviewCardView,
  ReviewSubmitResult,
  StudyAdvice,
  WordRecordView,
} from './content';

/**
 * 阶段 3–7 的对外视图：词典 / 生词本 / 复习 / 长难句分析 / 水平评估 / 后台。
 *
 * 与 `content.ts` 的分工：那里是"内容与进度"，这里是"学习行为与评估"。
 * 两者都遵守同一条约定 —— **描述接口返回的形状，不是数据库行的形状**：
 * 所有 `*_json` 列在服务层解析成结构化字段后才出网。
 */

// ---------------------------------------------------------------------------
// 词典（阶段 3）
// ---------------------------------------------------------------------------

/**
 * 词条来源。降级链从左到右：
 * `builtin`（内置词表）→ `free-dict`（Free Dictionary API）→ `llm`（AI 补释义）→ `manual`（人工/后台导入）。
 * `cache` 不是数据源而是"这次是从 KV/D1 读到的"，与 `cached` 字段重复出现是有意的：
 * 前端展示时要区分"这个词本来就有释义"和"这次走了缓存"。
 */
export type DictSource = 'builtin' | 'free-dict' | 'llm' | 'manual' | 'cache' | 'none';

export interface DictExample {
  en: string;
  zh: string | null;
}

/** 一个词条。字段全部可空 —— 词典是外部数据，"缺"是常态而非异常。 */
export interface DictEntry {
  /** 规范化 lemma，也是 `words.id` */
  lemma: string;
  /** 展示用的原形（可带大小写与重音，如 `US`） */
  display: string;
  phoneticUk: string | null;
  phoneticUs: string | null;
  audioUkUrl: string | null;
  audioUsUrl: string | null;
  /** 词性列表，如 ['n.', 'v.'] */
  pos: string[];
  defsEn: string[];
  defsZh: string[];
  examples: DictExample[];
  /** 变形表：`过去式` → `went` 等，键是中文标签 */
  exchange: Record<string, string>;
  cefrLevel: CefrLevel | null;
  /** COCA 词频排名（越小越常见），后端可空 */
  cocaRank: number | null;
  /** 词频 zipf 值（1–7），越高越常见 */
  zipf: number | null;
  source: DictSource;
}

/** 文中义：这个词**在这一句里**是什么意思。由 AI（或规则）给出，与通用词条分开存。 */
export interface SenseInContext {
  pos: string | null;
  senseZh: string | null;
  senseEn: string | null;
  /** 0–1；规则消歧的结果通常低于 0.5 */
  confidence: number | null;
  provider: AiProvider;
}

export interface DictionaryLookupResult {
  /** 用户点的那串字符 */
  query: string;
  /** 实际命中的词条键（可能是 lemma 候选里的某一个） */
  matched: string | null;
  entry: DictEntry | null;
  senseInContext: SenseInContext | null;
  /** 依次探测过的候选，便于解释"为什么点 studies 出来的是 study" */
  candidates: string[];
  /** 命中 KV/D1 缓存（未打上游） */
  cached: boolean;
  /** 是否走了降级链（上游失败或未命中，用内置词表兜底） */
  degraded: boolean;
  /** 面向用户的中文说明，例如「上游词典暂时不可用，展示内置词表释义」 */
  notes: string[];
}

// ---------------------------------------------------------------------------
// 生词本（阶段 4）
// ---------------------------------------------------------------------------

export interface WordbookStats {
  /** 按状态计数，键与 WORD_STATUSES 对齐 */
  byStatus: Record<string, number>;
  total: number;
  dueToday: number;
}

export interface WordbookListResult {
  items: WordRecordView[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  stats: WordbookStats;
}

/** 复习结算：一次会话的整体反馈，用于"今天复习完成"的收尾页。 */
export interface ReviewSummary {
  reviewed: number;
  /** 评分 ≥ 2 视为"记住了" */
  recalled: number;
  /** 评分 0 的次数 */
  forgotten: number;
  durationMs: number;
  /** 各评分档的次数，用于展示分布 */
  ratingCounts: Record<SrsRating, number>;
  /** 会话结束后最早到期的卡（继续按钮据此显示"还有 N 张"） */
  nextDueAt: number | null;
  /** 本次会话的人话总结 */
  message: string;
}

export interface ReviewSessionResult {
  results: ReviewSubmitResult[];
  summary: ReviewSummary;
}

export interface ReviewQueueResult {
  items: ReviewCardView[];
  summary: {
    /** 到期卡总数（不限于本次 limit） */
    dueTotal: number;
    /** 新卡（从未复习过）数量 */
    newCount: number;
    /** 今天已复习次数 */
    reviewedToday: number;
    /** 今天还要复习多少 */
    remainingToday: number;
  };
  mode: 'due' | 'mixed';
}

// ---------------------------------------------------------------------------
// 长难句分析（阶段 5）
// ---------------------------------------------------------------------------

export interface SentenceClauseView {
  type: ClauseType;
  /** 引导词原文（`which` / `even though`），非谓语结构为 null */
  marker: string | null;
  /** 该从句的原文片段 */
  text: string;
  /** 中文角色说明，如「修饰前面的名词」 */
  role: string;
}

export interface SentenceModifierView {
  text: string;
  role: string;
}

export interface GrammarPointView {
  title: string;
  detail: string;
}

export interface ImitationView {
  /** 句型骨架，如 `It is + adj. + that …` */
  pattern: string;
  /** 仿写示例（英文） */
  example: string;
  /** 示例的中文意思 */
  exampleZh: string | null;
}

/**
 * 长难句解析结果。
 *
 * `generatedBy` 是这张卡片最诚实的一个字段：
 * - `llm`：真调了模型；
 * - `rule`：**降级结果**，由 `shared/lib/ruleAnalysis` 用规则拼出来的"基础解析"，
 *   前端必须显式标注"基础解析（AI 暂不可用）"，不能让用户以为这是模型的结论。
 */
export interface SentenceAnalysisView {
  text: string;
  /** 主干（去掉修饰后的核心句） */
  mainClause: string;
  modifiers: SentenceModifierView[];
  clauses: SentenceClauseView[];
  grammarPoints: GrammarPointView[];
  /** 中文翻译；降级时为空串 */
  translation: string;
  /** 拆解步骤：怎么一步步读懂这句话 */
  steps: string[];
  /** 学习方法建议（朗读、回译、仿写……） */
  studyTips: string[];
  imitation: ImitationView[];
  keyWords: { surface: string; zh: string | null; cefrLevel: CefrLevel | null }[];
}

export interface SentenceAnalysisResult {
  analysis: SentenceAnalysisView;
  generatedBy: 'llm' | 'rule';
  provider: AiProvider;
  model: string | null;
  /** 命中缓存（D1 ai_cache 或 KV） */
  cached: boolean;
  /** 该句的规则侧特征，用于"为什么判为难句" */
  features: {
    isLong: boolean;
    score: number;
    reasons: LongSentenceReason[];
    notes: string[];
  };
  /** 降级原因等中文说明 */
  notes: string[];
  latencyMs: number;
}

/** 长难句实验室的列表项：一句 + 它属于哪篇文章。 */
export interface LongSentenceItemView {
  sentenceId: string;
  articleId: string;
  articleTitle: string;
  articleTitleZh: string | null;
  globalIndex: number;
  text: string;
  tokenCount: number;
  score: number;
  reasons: LongSentenceReason[];
  /** 是否已有缓存解析（前端据此显示"已解析"） */
  analyzed: boolean;
}

// ---------------------------------------------------------------------------
// 水平评估（阶段 6）
// ---------------------------------------------------------------------------

/** 评估输入特征快照。**必须可复现**：同样的特征必然得到同样的等级。 */
export interface LevelFeatures {
  /** 读过的文章数（有阅读进度记录的） */
  articleCount: number;
  finishedArticles: number;
  /** 文中查过的词（去重） */
  lookupWords: number;
  /** 生词本规模 */
  wordRecords: number;
  /** 每 100 词的查词次数：越高说明读得越吃力 */
  lookupsPer100Words: number;
  /** 标记为 unknown/fuzzy 的比例（0–1） */
  unknownRatio: number;
  /** 读过文章的平均难度（0–100）；没读过为 null */
  avgArticleDifficulty: number | null;
  /** 读完自评均值（1–5）；未评为 null */
  avgSelfRating: number | null;
  /** 阅读速度（词/分钟）；无法计算为 null */
  readingWpm: number | null;
  /** 复习过的卡片数 */
  reviewedCards: number;
  /** 复习中"记得"及以上的比例（0–1）；未复习为 null */
  retentionRate: number | null;
  /** 已掌握（mastered）词数 */
  masteredWords: number;
}

export interface WeaknessItemView {
  /** 机器可读的类别，前端按它选图标 */
  type: 'vocabulary' | 'syntax' | 'speed' | 'retention' | 'consistency' | 'breadth';
  label: string;
  /** 0–100，越高越弱 */
  score: number;
  /** 依据（可上屏的一句话，含具体数字） */
  evidence: string;
  advice: string;
}

export interface StudyPathStepView {
  order: number;
  title: string;
  detail: string;
  /** 建议持续天数 */
  days: number;
}

export interface LevelAssessmentView {
  id: string;
  articleId: string | null;
  cefr: CefrLevel;
  cefrConfidence: number;
  vocabEstimate: number;
  vocabMin: number | null;
  vocabMax: number | null;
  score: number | null;
  modelVersion: string;
  features: LevelFeatures;
  weaknesses: WeaknessItemView[];
  path: StudyPathStepView[];
  summaryText: string | null;
  createdAt: number;
}

export interface LevelReportView {
  latest: LevelAssessmentView | null;
  /** 历史评估（时间倒序，最多 20 条） */
  history: Pick<LevelAssessmentView, 'id' | 'cefr' | 'score' | 'vocabEstimate' | 'createdAt'>[];
  /** 当前特征（未被评估快照覆盖的实时值） */
  features: LevelFeatures;
  /** 下次什么时候再评估（中文说明） */
  nextAssessHint: string;
}

// ---------------------------------------------------------------------------
// 学习建议（阶段 7）
// ---------------------------------------------------------------------------

export interface LearningAdviceResult {
  articleId: string;
  articleTitle: string;
  mode: StudyMode;
  /** 文章难度对应的 CEFR，用于判断"这篇适不适合你" */
  articleCefr: CefrLevel | null;
  /** 与用户水平的匹配度说明，如「略高于你的当前水平，适合精读」 */
  levelFit: string;
  advice: StudyAdvice;
  generatedBy: 'rule' | 'llm';
  cached: boolean;
  notes: string[];
}

// ---------------------------------------------------------------------------
// 管理后台（阶段 7）
// ---------------------------------------------------------------------------

export interface AiCacheItemView {
  id: string;
  namespace: string;
  provider: string;
  model: string | null;
  promptVersion: string | null;
  status: string;
  hitCount: number;
  tokensIn: number | null;
  tokensOut: number | null;
  latencyMs: number | null;
  createdAt: number;
  expiresAt: number | null;
  /** 请求/响应摘要（截断到 200 字），只用于后台排查 */
  requestPreview: string | null;
  responsePreview: string | null;
  errorMessage: string | null;
}

export interface AiCacheListResult {
  items: AiCacheItemView[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  stats: {
    byNamespace: Record<string, number>;
    okCount: number;
    errorCount: number;
    totalHits: number;
    /** 平均延迟（毫秒），无数据为 null */
    avgLatencyMs: number | null;
  };
}

/** 后台用户列表项。刻意不含 email 之外的任何凭据类字段。 */
export interface AdminUserView {
  id: string;
  email: string | null;
  displayName: string;
  role: string;
  status: string;
  currentCefr: CefrLevel | null;
  wordCount: number;
  createdAt: number;
  lastLoginAt: number | null;
  deletedAt: number | null;
}

export interface AdminOverview {
  users: { total: number; active: number; newThisWeek: number; admins: number };
  content: {
    textbooks: number;
    units: number;
    articles: number;
    publicArticles: number;
    sentences: number;
    longSentences: number;
  };
  study: { wordRecords: number; reviewLogs: number; assessments: number; events: number };
  ai: { cacheEntries: number; errorEntries: number; totalHits: number; avgLatencyMs: number | null };
}

// ---------------------------------------------------------------------------
// 导出（阶段 7）
// ---------------------------------------------------------------------------

export const EXPORT_FORMATS = ['csv', 'anki', 'json'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

/** 导出说明：接口返回文件流时，这些信息放在响应头里（前端据此命名文件）。 */
export interface ExportMeta {
  format: ExportFormat;
  filename: string;
  /** 包含的词条数 */
  count: number;
}
